import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Album, Image } from '../types'
import ImageUpload from '../components/ImageUpload'
import ImageLightbox from '../components/ImageLightbox'
import MasonryGrid from '../components/MasonryGrid'
import CommentSection from '../components/CommentSection'
import { useAuthStore } from '../stores/authStore'
import { API_BASE_URL } from '../config'

const AlbumPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [album, setAlbum] = useState<Album | null>(null)
  const [images, setImages] = useState<Image[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (id) {
      fetchAlbumData()
    }
  }, [id])

  const fetchAlbumData = async () => {
    try {
      setIsLoading(true)
      
      // 获取相册信息
      const albumResponse = await fetch(`${API_BASE_URL}/api/albums/${id}`)
      if (!albumResponse.ok) {
        throw new Error('相册不存在')
      }
      const albumData = await albumResponse.json()
      setAlbum(albumData)

      // 获取相册图片
      const imagesResponse = await fetch(`${API_BASE_URL}/api/albums/${id}/images`)
      if (imagesResponse.ok) {
        const imagesData = await imagesResponse.json()
        setImages(imagesData)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteImage = async (imageId: number) => {
    if (!confirm('确定要删除这张图片吗？')) return

    try {
      const response = await fetch(`${API_BASE_URL}/api/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.ok) {
        setImages(images.filter(img => img.id !== imageId))
      }
    } catch (error) {
      console.error('删除失败:', error)
    }
  }

  const handleUploadSuccess = () => {
    fetchAlbumData() // 重新加载图片
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !album) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || '相册不存在'}</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          返回首页
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 相册标题和描述 */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/')}
          className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          返回相册列表
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{album.title}</h1>
        {album.description && (
          <p className="text-gray-600">{album.description}</p>
        )}
      </div>

      {/* 管理员上传区域 */}
      {isAuthenticated && (
        <div className="mb-8 bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">上传图片</h2>
          <ImageUpload albumId={album.id} onUploadSuccess={handleUploadSuccess} />
        </div>
      )}

      {/* 图片网格 */}
      {images.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500">暂无图片</p>
          {isAuthenticated && (
            <p className="text-gray-400 mt-2">使用上方上传区域添加图片</p>
          )}
        </div>
      ) : (
        <MasonryGrid
          items={images.map(img => ({
            id: img.id,
            url: img.url,
            filename: img.filename,
          }))}
          columns={4}
          gap={12}
          onItemClick={(index) => {
            setLightboxIndex(index)
            setLightboxOpen(true)
          }}
          onDelete={handleDeleteImage}
          showDelete={isAuthenticated}
        />
      )}

      {/* 评论区域 */}
      <CommentSection albumId={album.id} />

      {/* 图片灯箱 */}
      <ImageLightbox
        images={images}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  )
}

export default AlbumPage
